import { Item } from '@factorialco/deprecated-design-system'
import { Form } from 'factorial-form'
import {
  A3innuvaContributionTypeDocument,
  A3innuvaContributionTypesFragment,
} from 'generated/resources/graphql'

import { observer } from 'mobx-react'

import DropdownSearchBar from 'modules/core/components/DropdownSearchBar'
import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useQuery } from 'modules/core/hooks/useQuery'
import i18n from 'modules/core/lib/i18n'

import { SpanishContractData } from 'modules/contracts/hooks/useFetchSpanishContractData'
import {
  contributionTypesToOptions,
  fullName,
} from 'modules/payroll_integrations_a3innuva/lib/a3innuvaHelpers'

interface Props {
  form: Form
  spanishContractData: SpanishContractData
}

const ContributionTypeField = ({ form, spanishContractData }: Props) => {
  const session = useCurrentSession()
  const contributionTypeName = form.get('es_contribution_type_payroll_integration_name').value
  const contributionTypeCode = form.get('es_contribution_type_payroll_integration_code').value
  const formattedContributionTypeCode =
    contributionTypeCode && contributionTypeCode.length === 1
      ? 0 + contributionTypeCode
      : contributionTypeCode

  const contributionTypeId = form.get('es_contribution_type_id').value
  const spainContributionType = contributionTypeId
    ? spanishContractData.contributionTypes.get(String(contributionTypeId))
    : null

  const setCurrentContributionType = (
    a3innuvaContributionTypes: A3innuvaContributionTypesFragment[]
  ) => {
    const currentContributionType = a3innuvaContributionTypes.find(
      ({ name }) => name === spainContributionType?.attributes.name
    )

    if (currentContributionType) {
      form.setValues({
        es_contribution_type_payroll_integration_code: currentContributionType.code,
        es_contribution_type_payroll_integration_name: currentContributionType.name,
      })
    }
  }

  const isFormInitialized = () => {
    if (!form.has('es_contribution_type_payroll_integration_name')) return false
    return (
      form.get('es_contribution_type_payroll_integration_name') ===
      spainContributionType?.attributes.name
    )
  }

  const { data: contributionTypesData, loading } = useQuery(A3innuvaContributionTypeDocument, {
    variables: {
      companyId: session.company.id,
      currentUserId: session.access.userId,
    },
  })
  const a3innuvaContributionTypes =
    contributionTypesData?.payrollIntegrationsA3innuva.contributionTypes ?? []

  if (!loading && !isFormInitialized()) {
    setCurrentContributionType(a3innuvaContributionTypes)
  }

  const options = contributionTypesToOptions(a3innuvaContributionTypes).sort((a, b) =>
    a.label.localeCompare(b.label)
  )

  const labelNotFound = () => {
    return options.length
      ? i18n.t(
          'contract_versions.forms.contract_extra_information.a3innuva.contribution_type_not_found'
        )
      : i18n.t('contract_versions.forms.contract_extra_information.a3innuva.label_not_found')
  }

  const onContributionTypeChange = (option: Item) => {
    const selectedContributionType = a3innuvaContributionTypes.find(
      ({ code }) => code === option.value
    )
    form.setValues({
      es_contribution_type_payroll_integration_code: selectedContributionType?.code,
      es_contribution_type_payroll_integration_name: selectedContributionType?.name,
      es_contribution_type_id: undefined,
    })
  }

  return (
    <DropdownSearchBar
      name={fullName(formattedContributionTypeCode, contributionTypeName)}
      label={i18n.t('contract_versions.flows.fix.steps.legal_and_extras.contribution_type.label')}
      onSelect={onContributionTypeChange}
      labelNotFound={labelNotFound()}
      data={options}
      searchLabel={i18n.t(
        'contract_versions.forms.contract_extra_information.a3innuva.search_label'
      )}
      loading={loading}
      popoverWidth="s400"
      ellipsis={false}
    />
  )
}

export default observer(ContributionTypeField)
